import React, { useEffect, useRef, useState } from 'react';
import { WEB_API_DASHBOARD } from '../../env';

const endpoint = `${WEB_API_DASHBOARD}/dga-user-service-survey/dashboard/monthly-report`;

export default function MonthlyReportExport() {
    const [options, setOptions] = useState(null);
    const [month, setMonth] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [retry, setRetry] = useState(0);
    const request = useRef(null);
    const downloading = useRef(false);

    useEffect(() => {
        const controller = new AbortController();
        setError('');
        fetch(`${endpoint}/options`, { signal: controller.signal })
            .then(async response => {
                if (!response.ok) throw new Error('ไม่สามารถโหลดเดือนรายงานได้');
                return response.json();
            })
            .then(data => {
                if (controller.signal.aborted) return;
                setOptions(data);
                setMonth(data.default_month);
            })
            .catch(err => {
                if (err.name !== 'AbortError') setError('ไม่สามารถโหลดเดือนรายงานได้ กรุณาลองใหม่');
            });
        return () => { controller.abort(); };
    }, [retry]);
    useEffect(() => () => request.current?.abort(), []);

    const valid = Boolean(options && /^\d{4}-(0[1-9]|1[0-2])$/.test(month)
        && month >= options.min_month && month <= options.max_month);

    const download = async event => {
        event.preventDefault();
        if (!valid || downloading.current) return;
        downloading.current = true;
        setBusy(true);
        setError('');
        setNotice('');
        const controller = new AbortController();
        request.current = controller;
        try {
            const response = await fetch(`${endpoint}/export`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ report_month: month }),
                signal: controller.signal,
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.message || 'ไม่สามารถสร้างรายงานได้ กรุณาลองใหม่');
            }
            if (!(response.headers.get('Content-Type') || '').includes('spreadsheetml.sheet')) {
                throw new Error('รูปแบบไฟล์ที่ได้รับไม่ถูกต้อง กรุณาลองใหม่');
            }
            const blob = await response.blob();
            if (controller.signal.aborted) return;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `law-portal-monthly-${month}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Give the browser time to start the download before releasing the blob.
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            const warningCount = Number(response.headers.get('X-Report-Warnings') || 0);
            setNotice(warningCount
                ? `สร้างไฟล์แล้ว พบข้อจำกัดข้อมูล ${warningCount} รายการ กรุณาอ่านส่วนหมายเหตุท้ายไฟล์ก่อนนำส่งรายงาน`
                : 'สร้างไฟล์แล้ว กรุณาตรวจสอบรายงานก่อนนำส่ง');
        } catch (err) {
            if (err.name !== 'AbortError') setError(err.message);
        } finally {
            downloading.current = false;
            if (!controller.signal.aborted) setBusy(false);
        }
    };

    return (
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm" aria-labelledby="monthly-report-title">
            <h2 id="monthly-report-title" className="text-primary font-semibold">รายงาน Excel ประจำเดือน สำหรับ DGA</h2>
            <p className="mt-2 text-sm text-slate-600">
                ใช้เดือนรายงานเดียวสำหรับทั้งไฟล์ ครอบคลุมทุกศักดิ์กฎหมาย ทุกกระทรวง และทุกหน่วยงาน
                โดยไม่อิงตัวกรอง Dashboard ด้านบน
            </p>
            <p className="mt-2 text-sm text-amber-700">
                ยอดคำนวณย้อนหลังจากสถานะปัจจุบัน ไม่ใช่ snapshot ณ สิ้นเดือน
                ข้อมูลย้อนหลังอาจเปลี่ยนเมื่อมีการแก้ไขสถานะภายหลัง
            </p>
            <form onSubmit={download} className="mt-4 flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-sm text-slate-600" htmlFor="monthly-report-month">
                    เดือนรายงาน (ค.ศ.)
                    <input id="monthly-report-month" type="month" value={month}
                        min={options?.min_month} max={options?.max_month}
                        disabled={!options || busy} required
                        onChange={event => { setMonth(event.target.value); setNotice(''); }}
                        className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2" />
                </label>
                <button type="submit" disabled={!valid || busy}
                    className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-50">
                    {busy ? 'กำลังสร้างรายงาน…' : 'ดาวน์โหลดรายงานรายเดือน (.xlsx)'}
                </button>
                {!options && error && <button type="button" onClick={() => setRetry(value => value + 1)}
                    className="text-sm text-sky-700 underline">ลองโหลดเดือนรายงานใหม่</button>}
            </form>
            <p className="mt-2 text-xs text-slate-500">
                เลือกได้ถึงเดือนที่สิ้นสุดแล้ว ตามเวลา Asia/Bangkok • ดาวน์โหลดไฟล์เท่านั้น ยังไม่ส่งอีเมล
            </p>
            {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
            {notice && <p role="status" className="mt-2 text-sm text-slate-700">{notice}</p>}
        </section>
    );
}
